import type { RaspadinhaContemplatedPrizeImage, RaspadinhaDayWinningSchedule } from '@/types/raspadinha-campaign'
import { buildCampaignCsvFilename, csvEscape, downloadCsvFile } from './csv-export-utils'
import { getContemplatedPrizeTitle } from './contemplated-prize-images'
import { buildCampaignDayNumberByDate, formatCampaignDateLabel, formatWinningTimeLabel } from './date-winning-schedules'
import { parseWinningTimeToMinutes } from './generate-random-winning-times'

export const WINNING_SCHEDULES_CSV_HEADER = [
  'Data',
  'Horário',
  'Prêmio do horário',
  'Status',
  'Quantidade de ganhadores',
  'Nomes dos ganhadores',
  'CPF / documentos',
  'Prêmios dos ganhadores',
  'Observações',
] as const

export type WinningScheduleCsvOptions = {
  contemplatedPrizes?: RaspadinhaContemplatedPrizeImage[]
}

export type WinningScheduleCsvRow = {
  dateLabel: string
  dateSortValue: string
  timeLabel: string
  timeSortValue: number
  slotPrizeLabel: string
  statusLabel: string
  winnersCount: number
  winnerNames: string
  winnerDocuments: string
  winnerPrizes: string
  winnerNotes: string
}

function joinWinnerField(values: readonly string[], separator = '; '): string {
  return values
    .map((value) => value.trim())
    .filter(Boolean)
    .join(separator)
}

function getTimeSortValue(time: string): number {
  const minutes = parseWinningTimeToMinutes(time)
  return minutes ?? Number.MAX_SAFE_INTEGER
}

export function collectWinningScheduleCsvRows(
  schedules: RaspadinhaDayWinningSchedule[],
  options: WinningScheduleCsvOptions = {}
): WinningScheduleCsvRow[] {
  const contemplatedPrizes = options.contemplatedPrizes ?? []
  const rows: WinningScheduleCsvRow[] = []

  const dayNumberByDate = buildCampaignDayNumberByDate(schedules)

  for (const schedule of schedules) {
    const dayNumber = dayNumberByDate.get(schedule.date)
    const dateLabel = formatCampaignDateLabel(schedule.date, dayNumber)

    for (const slot of schedule.time_slots) {
      rows.push({
        dateLabel,
        dateSortValue: schedule.date,
        timeLabel: formatWinningTimeLabel(slot.time),
        timeSortValue: getTimeSortValue(slot.time),
        slotPrizeLabel: getContemplatedPrizeTitle(contemplatedPrizes, slot.contemplated_prize_id),
        statusLabel: slot.is_active ? 'Ativo' : 'Inativo',
        winnersCount: slot.winners.length,
        winnerNames: joinWinnerField(slot.winners.map((winner) => winner.name)),
        winnerDocuments: joinWinnerField(slot.winners.map((winner) => winner.document)),
        winnerPrizes: joinWinnerField(slot.winners.map((winner) => winner.prize_label)),
        winnerNotes: joinWinnerField(slot.winners.map((winner) => winner.notes)),
      })
    }
  }

  return rows.sort((left, right) => {
    if (left.dateSortValue !== right.dateSortValue) {
      return left.dateSortValue.localeCompare(right.dateSortValue)
    }

    if (left.timeSortValue !== right.timeSortValue) {
      return left.timeSortValue - right.timeSortValue
    }

    return left.timeLabel.localeCompare(right.timeLabel, 'pt-BR', { sensitivity: 'base' })
  })
}

export function buildWinningSchedulesCsv(
  schedules: RaspadinhaDayWinningSchedule[],
  options: WinningScheduleCsvOptions = {}
): string {
  const rows = collectWinningScheduleCsvRows(schedules, options)

  const lines = [
    WINNING_SCHEDULES_CSV_HEADER.join(','),
    ...rows.map((row) =>
      [
        csvEscape(row.dateLabel),
        csvEscape(row.timeLabel),
        csvEscape(row.slotPrizeLabel),
        csvEscape(row.statusLabel),
        csvEscape(row.winnersCount),
        csvEscape(row.winnerNames),
        csvEscape(row.winnerDocuments),
        csvEscape(row.winnerPrizes),
        csvEscape(row.winnerNotes),
      ].join(',')
    ),
  ]

  return lines.join('\r\n')
}

export function buildWinningSchedulesCsvFilename(campaignSlug: string): string {
  return buildCampaignCsvFilename('horarios-contemplacao', campaignSlug)
}

export function downloadWinningSchedulesCsv(
  schedules: RaspadinhaDayWinningSchedule[],
  filename: string,
  options: WinningScheduleCsvOptions = {}
): void {
  downloadCsvFile(buildWinningSchedulesCsv(schedules, options), filename)
}
